import {
    resourceImageReferenceIdFactory,
    resourceImageReferenceIdZodSchema,
} from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/resource-image-reference-id";
import { resourceImageReferenceTagZodEnum } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/resource-image-reference-tags";
import { NotFoundError } from "@@/shared/errors/not-found-error";
import type { ImageReferenceWithRequiredStorageItemImageId } from "@@/shared/images/image-reference";
import type {
    StorageItemImageId} from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import {
    storageItemImageIdZodSchema,
} from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { requiredTranslatableZodSchema } from "@@/shared/translations/translation";
import { exists } from "@@/utilities/exists";
import { z } from "zod";

const resourceImageReferenceZodObject = z.object({
    _id: resourceImageReferenceIdZodSchema,
    _type: z.literal("RESOURCE_IMAGE_REFERENCE"),
    title: requiredTranslatableZodSchema,
    tags: z.array(resourceImageReferenceTagZodEnum),
    imageId: storageItemImageIdZodSchema,
    blurhash: z.string().optional(),
});
type ResourceImageReference = z.infer<typeof resourceImageReferenceZodObject>;

const resourceImageReferenceFactoryParamsZodObject =
    resourceImageReferenceZodObject.omit({
        _id: true,
        _type: true,
    });
type FactoryParams = z.infer<
    typeof resourceImageReferenceFactoryParamsZodObject
>;

const resourceImageReferenceFactory = {
    from: (params: FactoryParams): ResourceImageReference => {
        const result: ResourceImageReference = {
            ...params,
            _id: resourceImageReferenceIdFactory(),
            _type: "RESOURCE_IMAGE_REFERENCE",
        };
        return result;
    },
    fromImageReference: (params: {
        /** An image reference with a required storageItemImageId, only new images, not old */
        imageReference: ImageReferenceWithRequiredStorageItemImageId;
    }): ResourceImageReference => {
        const imageReference = params.imageReference;
        if (!imageReference.storageItemId) {
            throw new Error(
                `The image reference does not have a storage item id: ${JSON.stringify(
                    imageReference,
                )}`,
            );
        }
        const image: ResourceImageReference = {
            _id: resourceImageReferenceIdFactory(),
            _type: "RESOURCE_IMAGE_REFERENCE",
            imageId: imageReference.storageItemId,
            tags: [], // should be an accommodation resource image reference tag
            title: imageReference.meta.title,
            blurhash: imageReference.blurhash,
        };
        return image;
    },
    fromStorageImageIds: async (params: {
        imageIds: StorageItemImageId[];
        fetcher: (
            imageId: StorageItemImageId,
        ) => Promise<ImageReferenceWithRequiredStorageItemImageId | undefined>;
        options?: {
            doNotThrowWhenNotFound?: boolean;
            errorOnNotFoundFactory?: (imageId: StorageItemImageId) => Error;
        };
    }): Promise<ResourceImageReference[]> => {
        const { imageIds, fetcher } = params;
        const images: ResourceImageReference[] = (
            await Promise.all(
                imageIds.map(async imageId => {
                    const imageReference = await fetcher(imageId);
                    if (!imageReference) {
                        if (params.options?.doNotThrowWhenNotFound) return null;
                        if (params.options?.errorOnNotFoundFactory)
                            throw params.options.errorOnNotFoundFactory(
                                imageId,
                            );
                        throw new NotFoundError(
                            `Image not found in the storage: ${imageId}`,
                            imageId,
                        );
                    }
                    return resourceImageReferenceFactory.fromImageReference({
                        imageReference,
                    });
                }),
            )
        ).filter(exists);
        return images;
    },
};

export {
    resourceImageReferenceFactory,
    resourceImageReferenceFactoryParamsZodObject,
    resourceImageReferenceZodObject,
};
export type {
    ResourceImageReference,
    FactoryParams as ResourceImageReferenceFactoryParams,
};
